import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ReactFlow, { Background, Controls, MiniMap } from "reactflow";
import "reactflow/dist/style.css";
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { useAuth } from "../../../auth/AuthContext";
import { storage } from "../../../firebase";
import { useEdges, useNodes } from "../hooks";
import {
  addHasTypeEdge,
  addLocationEdge,
  createPlaceNodeFromPlaceResult,
  createEdge,
  createNode,
  DEFAULT_GRAPH_ID,
  deleteEdge,
  deleteNode,
  ensurePlaceTypeNode,
  ensureBaseSchema,
  findPlaceNodeByPlaceId,
  getInheritedPropertyDefs,
  getTypesForNode,
  getValuesForEntity,
  normalizeValue,
  removeValueEdge,
  upsertValueEdge,
  updateEdge,
  updateNode,
  validateValue,
} from "../graphStore";
import { validateJSON } from "../utils/validateJSON";
import { copyText } from "../utils/clipboard";
import { buildEdgeUri, buildNodeUri } from "../quiltUri";
import { useQuiltFocus } from "../context/QuiltFocusContext";
import { computeTypeState } from "../typeInference";
import { createBaseTypeSet } from "../migrations/importLegacy";
import LocationsSection from "../components/LocationsSection";
import PlaceDetailsSection from "../components/PlaceDetailsSection";
import LocationMapCanvas from "../components/LocationMapCanvas";
import AsOfControl from "../time/AsOfControl";
import { useQuiltTime } from "../time/timeContext";
import {
  fromDateTimeLocalValue,
  isOccurredInRange,
  isValidAt,
  isValidInRange,
  normalizeTimestamp,
  toDateTimeLocalValue,
} from "../time/timeUtils";

const VIEWS = ["All", "TOLOPOSOGO", "Types", "Map"];
const PRIVACY_OPTIONS = ["public", "private", "sensitive"];
const VALUE_TYPES = ["String", "Number", "Boolean", "Date", "Email", "PhoneE164"];
const ATTENTION_TOOLS = [
  {
    id: "PMI",
    label: "PMI",
    title: "Plus / Minus / Interesting",
    prompt: "Identify benefits, drawbacks, and interesting implications of the current idea.",
  },
  {
    id: "CAF",
    label: "CAF",
    title: "Consider All Factors",
    prompt: "List every relevant factor before deciding what to change next.",
  },
  {
    id: "C&S",
    label: "C&S",
    title: "Consequences & Sequel",
    prompt: "Trace short-term, medium-term, and long-term consequences.",
  },
  {
    id: "AGO",
    label: "AGO",
    title: "Aims / Goals / Objectives",
    prompt: "Clarify the concrete objective this subgraph or edit is trying to achieve.",
  },
  {
    id: "FIP",
    label: "FIP",
    title: "First Important Priorities",
    prompt: "Choose the highest-priority items before making additional links.",
  },
  {
    id: "APC",
    label: "APC",
    title: "Alternatives / Possibilities / Choices",
    prompt: "Generate alternatives for the current structure or relationship.",
  },
  {
    id: "OPV",
    label: "OPV",
    title: "Other People's Views",
    prompt: "Check the graph from other stakeholders' perspectives.",
  },
];

function getNodePosition(node, idx = 0) {
  if (Number.isFinite(Number(node?.ui?.x)) && Number.isFinite(Number(node?.ui?.y))) {
    return { x: Number(node.ui.x), y: Number(node.ui.y) };
  }
  return {
    x: 120 + (idx % 7) * 140,
    y: 90 + Math.floor(idx / 7) * 120,
  };
}

function stripSystemKeys(props = {}) {
  const next = { ...props };
  if (next.kind === "Type") delete next.kind;
  return next;
}

function stringifyPropValue(value) {
  if (value === null) return "null";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value ?? "");
}

function coercePropValue(rawValue, currentValue) {
  if (typeof currentValue === "boolean") return rawValue === "true";
  if (typeof currentValue === "number") {
    const next = Number(rawValue);
    return Number.isFinite(next) ? next : currentValue;
  }
  if (currentValue === null || typeof currentValue === "object") {
    try {
      return JSON.parse(rawValue);
    } catch (_error) {
      return currentValue;
    }
  }
  return rawValue;
}

function temporalRangeError(validFromRaw, validToRaw) {
  const from = fromDateTimeLocalValue(validFromRaw);
  const to = fromDateTimeLocalValue(validToRaw);
  if (from && to && to.getTime() < from.getTime()) {
    return "validTo must be greater than or equal to validFrom.";
  }
  return "";
}

function isValidDateOnly(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const [y, m, d] = String(value).split("-").map((part) => Number(part));
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

function computeAgeFromBirthdate(birthdateValue, atDate) {
  if (!isValidDateOnly(birthdateValue)) return null;
  const [y, m, d] = birthdateValue.split("-").map((part) => Number(part));
  const at = normalizeTimestamp(atDate) || new Date();
  let age = at.getUTCFullYear() - y;
  const monthDelta = at.getUTCMonth() + 1 - m;
  if (monthDelta < 0 || (monthDelta === 0 && at.getUTCDate() < d)) age -= 1;
  return age >= 0 ? age : null;
}

function computeNextBirthday(birthdateValue, atDate) {
  if (!isValidDateOnly(birthdateValue)) return null;
  const [, mRaw, dRaw] = birthdateValue.split("-").map((part) => Number(part));
  const at = normalizeTimestamp(atDate) || new Date();
  const year = at.getUTCFullYear();
  let next = new Date(Date.UTC(year, mRaw - 1, dRaw));
  if (Number.isNaN(next.getTime())) return null;
  if (next.getTime() < Date.UTC(year, at.getUTCMonth(), at.getUTCDate())) {
    next = new Date(Date.UTC(year + 1, mRaw - 1, dRaw));
  }
  const y = next.getUTCFullYear();
  const m = String(next.getUTCMonth() + 1).padStart(2, "0");
  const d = String(next.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function buildExplodedPositions(nodes, edges) {
  const nodeIds = nodes.map((node) => node.id);
  const nodeSet = new Set(nodeIds);
  const adjacency = new Map(nodeIds.map((id) => [id, new Set()]));

  edges.forEach((edge) => {
    const fromId = edge.from?.nodeId;
    const toId = edge.to?.nodeId;
    if (!nodeSet.has(fromId) || !nodeSet.has(toId)) return;
    adjacency.get(fromId).add(toId);
    adjacency.get(toId).add(fromId);
  });

  const components = [];
  const seen = new Set();
  nodeIds.forEach((rootId) => {
    if (seen.has(rootId)) return;
    const queue = [rootId];
    const component = [];
    seen.add(rootId);
    while (queue.length) {
      const currentId = queue.shift();
      component.push(currentId);
      (adjacency.get(currentId) || new Set()).forEach((nextId) => {
        if (seen.has(nextId)) return;
        seen.add(nextId);
        queue.push(nextId);
      });
    }
    components.push(component);
  });

  const cols = Math.max(1, Math.ceil(Math.sqrt(components.length)));
  const componentGapX = 900;
  const componentGapY = 700;
  const positionById = new Map();

  components.forEach((component, componentIndex) => {
    const gridX = componentIndex % cols;
    const gridY = Math.floor(componentIndex / cols);
    const centerX = 220 + gridX * componentGapX;
    const centerY = 180 + gridY * componentGapY;

    const sorted = [...component].sort((a, b) => {
      const aDegree = (adjacency.get(a) || new Set()).size;
      const bDegree = (adjacency.get(b) || new Set()).size;
      if (bDegree !== aDegree) return bDegree - aDegree;
      return String(a).localeCompare(String(b));
    });

    if (sorted.length === 1) {
      positionById.set(sorted[0], { x: centerX, y: centerY });
      return;
    }

    const radius = Math.max(180, 70 * Math.sqrt(sorted.length));
    sorted.forEach((nodeId, idx) => {
      const angle = (idx / sorted.length) * Math.PI * 2;
      positionById.set(nodeId, {
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      });
    });
  });

  return positionById;
}

export default function GraphExplorerPage() {
  const { user } = useAuth();
  const { pendingFocus, clearFocus } = useQuiltFocus();
  const {
    asOfEnabled,
    asOfDate,
    filterMode,
    rangeStart,
    rangeEnd,
    setAsOfDate,
    setAsOfEnabled,
    setLiveEnabled,
  } = useQuiltTime();
  const { nodes, loading: loadingNodes, error: nodesError } = useNodes(DEFAULT_GRAPH_ID);
  const { edges, loading: loadingEdges, error: edgesError } = useEdges(DEFAULT_GRAPH_ID);

  const [view, setView] = useState("All");
  const [layoutMode, setLayoutMode] = useState("saved");
  const [subgraphSession, setSubgraphSession] = useState({
    active: false,
    nodeIds: [],
  });
  const [activeAttentionToolId, setActiveAttentionToolId] = useState("");
  const [subgraphNodeQuery, setSubgraphNodeQuery] = useState("");
  const [subgraphConnectType, setSubgraphConnectType] = useState("RELATES_TO");
  const [subgraphConnectDirection, setSubgraphConnectDirection] = useState("out");
  const [subgraphLinkSourceId, setSubgraphLinkSourceId] = useState("");
  const [subgraphNodePositions, setSubgraphNodePositions] = useState({});
  const [subgraphHopCount, setSubgraphHopCount] = useState(1);
  const [subgraphViewMode, setSubgraphViewMode] = useState("Graph");
  const [subgraphSelectionNodeIds, setSubgraphSelectionNodeIds] = useState([]);
  const [subgraphLabelDraft, setSubgraphLabelDraft] = useState("");
  const [subgraphLibrarySearch, setSubgraphLibrarySearch] = useState("");
  const [activeSubgraphId, setActiveSubgraphId] = useState("");
  const [subgraphReferenceTargetId, setSubgraphReferenceTargetId] = useState("");
  const [subgraphReferenceDirection, setSubgraphReferenceDirection] = useState("out");
  const [search, setSearch] = useState("");
  const [typeSearch, setTypeSearch] = useState("");
  const [selectedTypeFilterId, setSelectedTypeFilterId] = useState("");
  const [selectedNodeId, setSelectedNodeId] = useState("");
  const [selectedEdgeId, setSelectedEdgeId] = useState("");
  const [nodeLabelDraft, setNodeLabelDraft] = useState("");
  const [nodePropsText, setNodePropsText] = useState("{}");
  const [edgePropsText, setEdgePropsText] = useState("{}");
  const [status, setStatus] = useState("");
  const [typeAddSearch, setTypeAddSearch] = useState("");
  const [parentTypeSearch, setParentTypeSearch] = useState("");
  const [linkSearch, setLinkSearch] = useState("");
  const [showPlaces, setShowPlaces] = useState(true);
  const [nodeTimeDraft, setNodeTimeDraft] = useState({ occurredAt: "", validFrom: "", validTo: "" });
  const [edgeTimeDraft, setEdgeTimeDraft] = useState({ occurredAt: "", validFrom: "", validTo: "" });
  const [propertySchemaSlots, setPropertySchemaSlots] = useState([]);
  const [propertyDrafts, setPropertyDrafts] = useState({});
  const [propertyKeyToDefId, setPropertyKeyToDefId] = useState({});
  const [typePropertySearch, setTypePropertySearch] = useState("");
  const [propertySchemaDraft, setPropertySchemaDraft] = useState({
    label: "",
    key: "",
    valueType: "String",
    unit: "",
    cardinality: "0..1",
    description: "",
    privacyDefault: "private",
    constraintPattern: "",
    constraintMin: "",
    constraintMax: "",
    constraintEnum: "",
  });
  const propertySaveTimersRef = useRef({});
  const nodeImageInputRef = useRef(null);
  const [uploadingNodeImage, setUploadingNodeImage] = useState(false);

  const runAction = useCallback(async (label, fn) => {
    try {
      await fn();
    } catch (error) {
      setStatus(`${label} failed: ${error?.message || "Unknown error"}`);
    }
  }, []);

  const allNodeById = useMemo(() => new Map(nodes.map((node, idx) => [node.id, { ...node, __idx: idx }])), [nodes]);
  const allEdgeById = useMemo(() => new Map(edges.map((edge) => [edge.id, edge])), [edges]);

  const timelineNodes = useMemo(
    () => {
      if (!asOfEnabled) return nodes;
      if (filterMode === "range") {
        return nodes.filter((node) => isValidInRange(node, rangeStart, rangeEnd) || isOccurredInRange(node, rangeStart, rangeEnd));
      }
      return nodes.filter((node) => isValidAt(node, asOfDate));
    },
    [nodes, asOfEnabled, filterMode, asOfDate, rangeStart, rangeEnd]
  );

  const timelineNodeIds = useMemo(() => new Set(timelineNodes.map((node) => node.id)), [timelineNodes]);

  const timelineEdges = useMemo(() => (
    edges.filter((edge) => {
      const fromId = edge?.from?.nodeId;
      const toId = edge?.to?.nodeId;
      if (!fromId || !toId) return false;
      if (!timelineNodeIds.has(fromId) || !timelineNodeIds.has(toId)) return false;
      if (!asOfEnabled) return true;
      if (filterMode === "range") {
        return isValidInRange(edge, rangeStart, rangeEnd) || isOccurredInRange(edge, rangeStart, rangeEnd);
      }
      return isValidAt(edge, asOfDate);
    })
  ), [edges, timelineNodeIds, asOfEnabled, filterMode, asOfDate, rangeStart, rangeEnd]);

  const nodeById = useMemo(
    () => new Map(timelineNodes.map((node, idx) => [node.id, { ...node, __idx: idx }])),
    [timelineNodes]
  );

  const typeState = useMemo(() => computeTypeState(timelineNodes, timelineEdges), [timelineNodes, timelineEdges]);

  const typeNodes = useMemo(
    () => timelineNodes.filter((node) => node.props?.kind === "Type").sort((a, b) => (a.label || "").localeCompare(b.label || "")),
    [timelineNodes]
  );

  useEffect(() => {
    if (!user?.uid) return;
    setStatus((prev) => prev || "Ready.");
  }, [user?.uid]);

  useEffect(() => {
    if (!pendingFocus) return;
    if (pendingFocus.kind === "node" && allNodeById.has(pendingFocus.id)) {
      setSelectedEdgeId("");
      setSelectedNodeId(pendingFocus.id);
      clearFocus();
      return;
    }
    if (pendingFocus.kind === "edge" && allEdgeById.has(pendingFocus.id)) {
      setSelectedNodeId("");
      setSelectedEdgeId(pendingFocus.id);
      clearFocus();
    }
  }, [pendingFocus, allNodeById, allEdgeById, clearFocus]);

  const selectedNode = selectedNodeId ? allNodeById.get(selectedNodeId) : null;
  const selectedEdge = selectedEdgeId ? allEdgeById.get(selectedEdgeId) : null;
  const isSelectedTypeNode = Boolean(selectedNode?.props?.kind === "Type");
  const isSelectedPropertyNode = Boolean(selectedNode?.props?.kind === "Property");
  const isSelectedEntityNode = Boolean(selectedNode && !isSelectedTypeNode && !isSelectedPropertyNode);

  useEffect(() => {
    if (!selectedNode) {
      setNodeLabelDraft("");
      setNodePropsText("{}");
      setNodeTimeDraft({ occurredAt: "", validFrom: "", validTo: "" });
      return;
    }
    setNodeLabelDraft(selectedNode.label || "");
    setNodePropsText(JSON.stringify(stripSystemKeys(selectedNode.props || {}), null, 2));
    setNodeTimeDraft({
      occurredAt: toDateTimeLocalValue(selectedNode.occurredAt),
      validFrom: toDateTimeLocalValue(selectedNode.validFrom),
      validTo: toDateTimeLocalValue(selectedNode.validTo),
    });
  }, [selectedNode]);

  useEffect(() => {
    if (!selectedEdge) {
      setEdgePropsText("{}");
      setEdgeTimeDraft({ occurredAt: "", validFrom: "", validTo: "" });
      return;
    }
    setEdgePropsText(JSON.stringify(selectedEdge.props || {}, null, 2));
    setEdgeTimeDraft({
      occurredAt: toDateTimeLocalValue(selectedEdge.occurredAt),
      validFrom: toDateTimeLocalValue(selectedEdge.validFrom),
      validTo: toDateTimeLocalValue(selectedEdge.validTo),
    });
  }, [selectedEdge]);

  useEffect(() => {
    let active = true;
    if (!selectedNode || !isSelectedEntityNode || !user?.uid) {
      setPropertySchemaSlots([]);
      setPropertyDrafts({});
      setPropertyKeyToDefId({});
      return undefined;
    }

    Promise.all([
      getInheritedPropertyDefs(DEFAULT_GRAPH_ID, user.uid, selectedNode.id),
      getValuesForEntity(DEFAULT_GRAPH_ID, user.uid, selectedNode.id),
    ])
      .then(([slots, values]) => {
        if (!active) return;
        setPropertySchemaSlots(slots);
        const byKey = {};
        const nextDrafts = {};
        slots.forEach((slot) => {
          byKey[slot.propertyDef.props?.key] = slot.propertyDefId;
          const existing = values?.[slot.propertyDefId];
          const baseValue = existing ? existing.value : slot.defaultValue;
          nextDrafts[slot.propertyDefId] = {
            value: slot.propertyDef.props?.valueType === "Boolean"
              ? Boolean(baseValue)
              : (baseValue ?? ""),
            privacy: existing?.privacy || slot.privacyDefault || "private",
            error: "",
          };
        });
        setPropertyDrafts(nextDrafts);
        setPropertyKeyToDefId(byKey);
      })
      .catch((_error) => {
        if (!active) return;
        setPropertySchemaSlots([]);
        setPropertyDrafts({});
        setPropertyKeyToDefId({});
      });

    return () => {
      active = false;
    };
  }, [selectedNode, isSelectedEntityNode, user?.uid]);

  useEffect(() => () => {
    Object.values(propertySaveTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    propertySaveTimersRef.current = {};
  }, []);

  useEffect(() => {
    if (!selectedNode || !isSelectedPropertyNode) {
      setPropertySchemaDraft({
        label: "",
        key: "",
        valueType: "String",
        unit: "",
        cardinality: "0..1",
        description: "",
        privacyDefault: "private",
        constraintPattern: "",
        constraintMin: "",
        constraintMax: "",
        constraintEnum: "",
      });
      return;
    }
    const props = selectedNode.props || {};
    const constraints = props.constraints && typeof props.constraints === "object" ? props.constraints : {};
    setPropertySchemaDraft({
      label: selectedNode.label || "",
      key: props.key || "",
      valueType: props.valueType || "String",
      unit: props.unit || "",
      cardinality: props.cardinality || "0..1",
      description: props.description || "",
      privacyDefault: props.privacyDefault || "private",
      constraintPattern: constraints.pattern || "",
      constraintMin: constraints.min == null ? "" : String(constraints.min),
      constraintMax: constraints.max == null ? "" : String(constraints.max),
      constraintEnum: Array.isArray(constraints.enum) ? constraints.enum.join(", ") : "",
    });
  }, [selectedNode, isSelectedPropertyNode]);

  const searchLower = search.trim().toLowerCase();
  const typeSearchLower = typeSearch.trim().toLowerCase();

  const typeLabelLookup = useMemo(() => {
    const map = new Map();
    typeNodes.forEach((typeNode) => map.set(typeNode.id, typeNode.label || typeNode.id));
    return map;
  }, [typeNodes]);

  const toloTypeId = useMemo(() => {
    const found = typeNodes.find((node) => (node.label || "").toLowerCase() === "toloposogo");
    return found?.id || "";
  }, [typeNodes]);

  const placeTypeId = useMemo(() => {
    const found = typeNodes.find((node) => (node.label || "").toLowerCase() === "place");
    return found?.id || "";
  }, [typeNodes]);

  const placeNodeIds = useMemo(() => (
    new Set(
      timelineNodes
        .filter((node) => node.props?.kind === "Place" || (placeTypeId && typeState.inferred.get(node.id)?.has(placeTypeId)))
        .map((node) => node.id)
    )
  ), [timelineNodes, placeTypeId, typeState.inferred]);

  const visibleNodeIds = useMemo(() => {
    if (subgraphSession.active) {
      return new Set(subgraphSession.nodeIds.filter((nodeId) => allNodeById.has(nodeId)));
    }
    let base = timelineNodes;

    if (view === "Types") {
      base = timelineNodes.filter((node) => node.props?.kind === "Type");
    }

    if (view === "TOLOPOSOGO" && toloTypeId) {
      base = base.filter((node) => typeState.inferred.get(node.id)?.has(toloTypeId));
    }

    if (selectedTypeFilterId) {
      base = base.filter((node) => typeState.inferred.get(node.id)?.has(selectedTypeFilterId));
    }

    if (searchLower) {
      base = base.filter((node) => (node.label || "").toLowerCase().includes(searchLower));
    }

    if (!showPlaces && view !== "Map") {
      base = base.filter((node) => {
        const inferredIsPlace = placeTypeId ? typeState.inferred.get(node.id)?.has(placeTypeId) : false;
        return !inferredIsPlace && node.props?.kind !== "Place";
      });
    }

    return new Set(base.map((node) => node.id));
  }, [
    timelineNodes,
    view,
    toloTypeId,
    typeState.inferred,
    selectedTypeFilterId,
    searchLower,
    showPlaces,
    placeTypeId,
    allNodeById,
    subgraphSession.active,
    subgraphSession.nodeIds,
  ]);

  const visibleNodes = useMemo(
    () => (subgraphSession.active ? nodes : timelineNodes).filter((node) => visibleNodeIds.has(node.id)),
    [subgraphSession.active, nodes, timelineNodes, visibleNodeIds]
  );

  const visibleEdges = useMemo(() => {
    const sourceEdges = subgraphSession.active ? edges : timelineEdges;
    let base = sourceEdges.filter((edge) => {
      const fromId = edge?.from?.nodeId;
      const toId = edge?.to?.nodeId;
      return Boolean(fromId && toId && visibleNodeIds.has(fromId) && visibleNodeIds.has(toId));
    });
    if (subgraphSession.active) return base;
    if (view === "Types") base = base.filter((edge) => edge.type === "IS_A");
    return base;
  }, [edges, timelineEdges, visibleNodeIds, view, subgraphSession.active]);
  const explodedPositionById = useMemo(
    () => buildExplodedPositions(visibleNodes, visibleEdges),
    [visibleNodes, visibleEdges]
  );
  const subgraphPositionById = useMemo(() => {
    if (!subgraphSession.active) return new Map();
    const ids = visibleNodes.map((node) => node.id);
    const centerX = 520;
    const centerY = 310;
    const positions = new Map();
    if (ids.length === 0) return positions;
    const focusId = selectedNodeId && ids.includes(selectedNodeId) ? selectedNodeId : ids[0];
    positions.set(focusId, { x: centerX, y: centerY });
    const others = ids.filter((id) => id !== focusId).sort((a, b) => {
      const aLabel = allNodeById.get(a)?.label || a;
      const bLabel = allNodeById.get(b)?.label || b;
      return aLabel.localeCompare(bLabel);
    });
    const radius = Math.max(180, 70 * Math.sqrt(Math.max(1, others.length)));
    others.forEach((id, idx) => {
      const angle = (idx / Math.max(1, others.length)) * Math.PI * 2;
      positions.set(id, {
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      });
    });
    return positions;
  }, [subgraphSession.active, visibleNodes, allNodeById, selectedNodeId]);

  const subgraphNodeCandidates = useMemo(() => {
    if (!subgraphSession.active) return [];
    const lower = subgraphNodeQuery.trim().toLowerCase();
    return nodes
      .filter((node) => !subgraphSession.nodeIds.includes(node.id))
      .filter((node) => !lower || (node.label || "").toLowerCase().includes(lower))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
      .slice(0, 16);
  }, [subgraphSession.active, subgraphNodeQuery, nodes, subgraphSession.nodeIds]);

  const subgraphEdgeTypeOptions = useMemo(() => {
    if (!subgraphSession.active) return [];
    const lower = String(subgraphConnectType || "").trim().toLowerCase();
    const types = [...new Set(timelineEdges.map((edge) => String(edge.type || "").trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
    return types
      .filter((type) => !lower || type.toLowerCase().includes(lower))
      .slice(0, 12);
  }, [subgraphSession.active, subgraphConnectType, timelineEdges]);
  const subgraphMasterSearchResults = useMemo(() => {
    if (!subgraphSession.active || !searchLower) return [];
    return nodes
      .filter((node) => (node.label || "").toLowerCase().includes(searchLower))
      .filter((node) => !subgraphSession.nodeIds.includes(node.id))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
      .slice(0, 14);
  }, [subgraphSession.active, searchLower, nodes, subgraphSession.nodeIds]);
  const subgraphLibraryNodes = useMemo(() => (
    nodes
      .filter((node) => node.props?.kind === "Subgraph")
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
  ), [nodes]);
  const subgraphLibraryResults = useMemo(() => {
    const lower = subgraphLibrarySearch.trim().toLowerCase();
    return subgraphLibraryNodes
      .filter((node) => !lower || (node.label || "").toLowerCase().includes(lower))
      .slice(0, 24);
  }, [subgraphLibraryNodes, subgraphLibrarySearch]);
  const activeSubgraphOutgoingRefs = useMemo(() => {
    if (!activeSubgraphId) return [];
    return edges
      .filter((edge) => edge.type === "REF_SUBGRAPH" && edge.from?.nodeId === activeSubgraphId)
      .map((edge) => edge.to?.nodeId)
      .filter(Boolean);
  }, [edges, activeSubgraphId]);
  const activeSubgraphIncomingRefs = useMemo(() => {
    if (!activeSubgraphId) return [];
    return edges
      .filter((edge) => edge.type === "REF_SUBGRAPH" && edge.to?.nodeId === activeSubgraphId)
      .map((edge) => edge.from?.nodeId)
      .filter(Boolean);
  }, [edges, activeSubgraphId]);
  const activeAttentionTool = useMemo(
    () => ATTENTION_TOOLS.find((tool) => tool.id === activeAttentionToolId) || null,
    [activeAttentionToolId]
  );

  const typeFilterOptions = useMemo(
    () => typeNodes.filter((node) => !typeSearchLower || (node.label || "").toLowerCase().includes(typeSearchLower)),
    [typeNodes, typeSearchLower]
  );

  const addTypeOptions = useMemo(() => {
    const lower = typeAddSearch.trim().toLowerCase();
    return typeNodes.filter((node) => !lower || (node.label || "").toLowerCase().includes(lower));
  }, [typeNodes, typeAddSearch]);

  const parentTypeOptions = useMemo(() => {
    const lower = parentTypeSearch.trim().toLowerCase();
    return typeNodes.filter((node) => !lower || (node.label || "").toLowerCase().includes(lower));
  }, [typeNodes, parentTypeSearch]);

  const nodeLabelOptions = useMemo(() => {
    const lower = linkSearch.trim().toLowerCase();
    return timelineNodes.filter((node) => !lower || (node.label || "").toLowerCase().includes(lower));
  }, [timelineNodes, linkSearch]);

  const selectedNodeDirectTypeEdges = useMemo(() => {
    if (!selectedNode) return [];
    return timelineEdges.filter((edge) => edge.type === "HAS_TYPE" && edge.from.nodeId === selectedNode.id);
  }, [selectedNode, timelineEdges]);

  const [selectedNodeInferredTypeLabels, setSelectedNodeInferredTypeLabels] = useState([]);
  const parsedNodeProps = useMemo(() => validateJSON(nodePropsText), [nodePropsText]);
  const nodeTemporalError = useMemo(
    () => temporalRangeError(nodeTimeDraft.validFrom, nodeTimeDraft.validTo),
    [nodeTimeDraft.validFrom, nodeTimeDraft.validTo]
  );
  const edgeTemporalError = useMemo(
    () => temporalRangeError(edgeTimeDraft.validFrom, edgeTimeDraft.validTo),
    [edgeTimeDraft.validFrom, edgeTimeDraft.validTo]
  );

  useEffect(() => {
    let active = true;
    if (!selectedNode || !user?.uid) {
      setSelectedNodeInferredTypeLabels([]);
      return undefined;
    }
    getTypesForNode(DEFAULT_GRAPH_ID, user.uid, selectedNode.id)
      .then((rows) => {
        if (!active) return;
        setSelectedNodeInferredTypeLabels(rows.map((item) => item.label || item.id));
      })
      .catch(() => {
        if (!active) return;
        setSelectedNodeInferredTypeLabels([]);
      });
    return () => {
      active = false;
    };
  }, [selectedNode, user?.uid]);

  const selectedTypeParents = useMemo(() => {
    if (!selectedNode || selectedNode.props?.kind !== "Type") return [];
    return timelineEdges.filter((edge) => edge.type === "IS_A" && edge.from.nodeId === selectedNode.id);
  }, [selectedNode, timelineEdges]);

  const selectedTypeChildren = useMemo(() => {
    if (!selectedNode || selectedNode.props?.kind !== "Type") return [];
    return timelineEdges.filter((edge) => edge.type === "IS_A" && edge.to.nodeId === selectedNode.id);
  }, [selectedNode, timelineEdges]);

  const selectedNodeRefersTo = useMemo(() => {
    if (!selectedNode) return [];
    return timelineEdges.filter((edge) => edge.type === "REFERS_TO" && edge.from.nodeId === selectedNode.id);
  }, [selectedNode, timelineEdges]);
  const selectedNodeConnections = useMemo(() => {
    if (!selectedNode) return [];
    return timelineEdges
      .filter((edge) => edge.from?.nodeId === selectedNode.id || edge.to?.nodeId === selectedNode.id)
      .map((edge) => {
        const outgoing = edge.from?.nodeId === selectedNode.id;
        const neighborId = outgoing ? edge.to?.nodeId : edge.from?.nodeId;
        return {
          edgeId: edge.id,
          edgeType: edge.type || "RELATES_TO",
          direction: outgoing ? "out" : "in",
          neighborId,
          neighborLabel: nodeById.get(neighborId)?.label || allNodeById.get(neighborId)?.label || neighborId,
        };
      })
      .filter((row) => row.neighborId)
      .sort((a, b) => {
        const labelCmp = String(a.neighborLabel || "").localeCompare(String(b.neighborLabel || ""));
        if (labelCmp !== 0) return labelCmp;
        return String(a.edgeType || "").localeCompare(String(b.edgeType || ""));
      });
  }, [selectedNode, timelineEdges, nodeById, allNodeById]);
  const selectedNodeConnectionGroups = useMemo(() => {
    const groups = new Map();
    selectedNodeConnections.forEach((connection) => {
      const key = `${connection.direction}|${connection.edgeType}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          direction: connection.direction,
          edgeType: connection.edgeType,
          rows: [],
        });
      }
      groups.get(key).rows.push(connection);
    });

    return [...groups.values()]
      .map((group) => ({
        ...group,
        rows: group.rows.sort((a, b) => String(a.neighborLabel || "").localeCompare(String(b.neighborLabel || ""))),
      }))
      .sort((a, b) => {
        const typeCmp = String(a.edgeType || "").localeCompare(String(b.edgeType || ""));
        if (typeCmp !== 0) return typeCmp;
        if (a.direction === b.direction) return 0;
        return a.direction === "out" ? -1 : 1;
      });
  }, [selectedNodeConnections]);
  const outgoingConnectionGroups = useMemo(
    () => selectedNodeConnectionGroups.filter((group) => group.direction === "out"),
    [selectedNodeConnectionGroups]
  );
  const incomingConnectionGroups = useMemo(
    () => selectedNodeConnectionGroups.filter((group) => group.direction === "in"),
    [selectedNodeConnectionGroups]
  );

  const isSelectedPlaceNode = Boolean(
    selectedNode
    && (placeNodeIds.has(selectedNode.id) || selectedNode.props?.kind === "Place")
  );
  const selectedNodeImages = useMemo(() => {
    if (!selectedNode) return [];
    const rawImages = Array.isArray(selectedNode.props?.images) ? selectedNode.props.images : [];
    const normalized = rawImages
      .map((item, idx) => {
        if (typeof item === "string") {
          return {
            id: `legacy-${idx}`,
            name: "",
            url: item,
            path: "",
          };
        }
        if (!item || typeof item !== "object" || !item.url) return null;
        return {
          id: item.path || item.url || `img-${idx}`,
          name: String(item.name || ""),
          url: String(item.url),
          path: String(item.path || ""),
          contentType: item.contentType || "",
          size: Number(item.size) || 0,
          createdAt: item.createdAt || "",
        };
      })
      .filter(Boolean);
    if (!normalized.length && selectedNode.props?.imageUrl) {
      normalized.push({
        id: "legacy-imageUrl",
        name: "",
        url: String(selectedNode.props.imageUrl),
        path: String(selectedNode.props.imagePath || ""),
      });
    }
    return normalized;
  }, [selectedNode]);

  const typePropertyEdges = useMemo(() => {
    if (!selectedNode || !isSelectedTypeNode) return [];
    return timelineEdges.filter((edge) => edge.type === "HAS_PROPERTY" && edge.from?.nodeId === selectedNode.id);
  }, [selectedNode, isSelectedTypeNode, timelineEdges]);

  const propertyNodes = useMemo(
    () => timelineNodes.filter((node) => node.props?.kind === "Property").sort((a, b) => (a.label || "").localeCompare(b.label || "")),
    [timelineNodes]
  );

  const filteredPropertyNodes = useMemo(() => {
    const lower = typePropertySearch.trim().toLowerCase();
    return propertyNodes.filter((node) => {
      if (!lower) return true;
      return (node.label || "").toLowerCase().includes(lower) || String(node.props?.key || "").toLowerCase().includes(lower);
    });
  }, [propertyNodes, typePropertySearch]);

  const createBlankNode = async (isType = false) => {
    if (!user?.uid) return;
    const ref = await createNode(DEFAULT_GRAPH_ID, {
      label: isType ? "New Type" : "New Node",
      props: isType ? { kind: "Type" } : {},
      ui: { x: 180, y: 120 },
      ownerUid: user.uid,
    });
    setSelectedEdgeId("");
    setSelectedNodeId(ref.id);
  };

  const createPropertyNode = async () => {
    if (!user?.uid) return;
    const ref = await createNode(DEFAULT_GRAPH_ID, {
      label: "New Property",
      props: {
        kind: "Property",
        key: "",
        valueType: "String",
        unit: null,
        cardinality: "0..1",
        description: "",
        constraints: {},
        privacyDefault: "private",
      },
      ownerUid: user.uid,
      ui: { x: 200, y: 140 },
    });
    setSelectedEdgeId("");
    setSelectedNodeId(ref.id);
  };

  const createEventNode = async () => {
    if (!user?.uid) return;
    const ref = await createNode(DEFAULT_GRAPH_ID, {
      label: "Event",
      props: {},
      occurredAt: new Date(),
      ownerUid: user.uid,
      ui: { x: 220, y: 160 },
    });
    setSelectedEdgeId("");
    setSelectedNodeId(ref.id);
  };

  const createBlankEdge = async () => {
    if (!user?.uid || timelineNodes.length < 2) return;
    const from = selectedNodeId || timelineNodes[0].id;
    const to = timelineNodes.find((node) => node.id !== from)?.id || timelineNodes[0].id;
    const ref = await createEdge(DEFAULT_GRAPH_ID, {
      type: "RELATES_TO",
      from: { nodeId: from },
      to: { nodeId: to },
      props: {},
      ownerUid: user.uid,
    });
    setSelectedNodeId("");
    setSelectedEdgeId(ref.id);
  };

  const createStateEdge = async () => {
    if (!user?.uid || timelineNodes.length < 2) return;
    const from = selectedNodeId || timelineNodes[0].id;
    const to = timelineNodes.find((node) => node.id !== from)?.id || timelineNodes[0].id;
    const ref = await createEdge(DEFAULT_GRAPH_ID, {
      type: "RELATES_TO",
      from: { nodeId: from },
      to: { nodeId: to },
      props: {},
      validFrom: new Date(),
      validTo: null,
      ownerUid: user.uid,
    });
    setSelectedNodeId("");
    setSelectedEdgeId(ref.id);
  };

  const startSubgraphSession = () => {
    setSelectedEdgeId("");
    setSelectedNodeId("");
    setSubgraphSession({
      active: true,
      nodeIds: [],
    });
    setSubgraphNodeQuery("");
    setSubgraphConnectType("RELATES_TO");
    setSubgraphConnectDirection("out");
    setSubgraphLinkSourceId("");
    setSubgraphNodePositions({});
    setSubgraphHopCount(1);
    setSubgraphViewMode("Graph");
    setSubgraphSelectionNodeIds([]);
    setSubgraphLabelDraft("");
    setSubgraphLibrarySearch("");
    setActiveSubgraphId("");
    setSubgraphReferenceTargetId("");
    setSubgraphReferenceDirection("out");
    setStatus("Subgraph session started.");
  };

  const finishSubgraphSession = () => {
    setSubgraphSession({ active: false, nodeIds: [] });
    setSubgraphNodeQuery("");
    setSubgraphLinkSourceId("");
    setSubgraphNodePositions({});
    setSubgraphHopCount(1);
    setSubgraphViewMode("Graph");
    setSubgraphSelectionNodeIds([]);
    setSubgraphLabelDraft("");
    setSubgraphLibrarySearch("");
    setActiveSubgraphId("");
    setSubgraphReferenceTargetId("");
    setSubgraphReferenceDirection("out");
    setStatus("Subgraph session finished.");
  };

  const connectTwoSubgraphNodes = async (sourceNodeId, targetNodeId) => {
    if (!user?.uid || !sourceNodeId || !targetNodeId || sourceNodeId === targetNodeId) return;
    const fromNodeId = subgraphConnectDirection === "out" ? sourceNodeId : targetNodeId;
    const toNodeId = subgraphConnectDirection === "out" ? targetNodeId : sourceNodeId;
    await createEdge(DEFAULT_GRAPH_ID, {
      type: (subgraphConnectType || "RELATES_TO").trim().toUpperCase() || "RELATES_TO",
      from: { nodeId: fromNodeId },
      to: { nodeId: toNodeId },
      props: {},
      ownerUid: user.uid,
    });
    setSubgraphSession((prev) => {
      const next = new Set(prev.nodeIds);
      next.add(sourceNodeId);
      next.add(targetNodeId);
      return { ...prev, nodeIds: [...next] };
    });
    setSelectedEdgeId("");
    setSelectedNodeId(targetNodeId);
    setStatus("Nodes connected in subgraph session.");
  };

  const includeNeighborsForSelectedNode = (hopCount = 1) => {
    if (!subgraphSession.active || !selectedNode) return;
    const hops = Math.max(1, Number(hopCount) || 1);
    const adjacency = new Map();
    edges.forEach((edge) => {
      const fromId = edge.from?.nodeId;
      const toId = edge.to?.nodeId;
      if (!fromId || !toId) return;
      if (!adjacency.has(fromId)) adjacency.set(fromId, new Set());
      if (!adjacency.has(toId)) adjacency.set(toId, new Set());
      adjacency.get(fromId).add(toId);
      adjacency.get(toId).add(fromId);
    });

    const seen = new Set([selectedNode.id]);
    let frontier = [selectedNode.id];
    for (let depth = 0; depth < hops; depth += 1) {
      const nextFrontier = [];
      frontier.forEach((nodeId) => {
        (adjacency.get(nodeId) || new Set()).forEach((neighborId) => {
          if (seen.has(neighborId)) return;
          seen.add(neighborId);
          nextFrontier.push(neighborId);
        });
      });
      frontier = nextFrontier;
      if (!frontier.length) break;
    }

    const uniqueNeighborIds = [...seen].filter((id) => id !== selectedNode.id);
    if (uniqueNeighborIds.length === 0) {
      setStatus(`No neighbors found within ${hops} hop(s).`);
      return;
    }

    setSubgraphSession((prev) => {
      const next = new Set(prev.nodeIds);
      next.add(selectedNode.id);
      uniqueNeighborIds.forEach((id) => next.add(id));
      return { ...prev, nodeIds: [...next] };
    });
    setStatus(`Added ${uniqueNeighborIds.length} node(s) within ${hops} hop(s) to subgraph session.`);
  };

  const connectNodeInSubgraphSession = async (targetNodeId, sourceNodeId = selectedNode?.id) => {
    if (!user?.uid || !sourceNodeId || !targetNodeId || !subgraphSession.active) return;
    await connectTwoSubgraphNodes(sourceNodeId, targetNodeId);
  };

  const addNodeToSubgraphSession = (nodeId) => {
    if (!nodeId || !subgraphSession.active) return;
    setSubgraphSession((prev) => (
      prev.nodeIds.includes(nodeId)
        ? prev
        : { ...prev, nodeIds: [...prev.nodeIds, nodeId] }
    ));
    setSelectedEdgeId("");
    setSelectedNodeId(nodeId);
  };

  const getSubgraphMemberIds = useCallback((subgraphNodeId) => (
    edges
      .filter((edge) => edge.type === "IN_SUBGRAPH" && edge.to?.nodeId === subgraphNodeId)
      .map((edge) => edge.from?.nodeId)
      .filter(Boolean)
  ), [edges]);

  const ensureSubgraphNodeByLabel = useCallback(async (label) => {
    const normalized = String(label || "").trim();
    if (!normalized || !user?.uid) return null;
    const existing = nodes.find(
      (node) => node.props?.kind === "Subgraph" && (node.label || "").toLowerCase() === normalized.toLowerCase()
    );
    if (existing) return existing;
    const ref = await createNode(DEFAULT_GRAPH_ID, {
      label: normalized,
      props: { kind: "Subgraph" },
      ownerUid: user.uid,
      ui: { x: 240, y: 180 },
    });
    return {
      id: ref.id,
      label: normalized,
      props: { kind: "Subgraph" },
      ownerUid: user.uid,
    };
  }, [nodes, user?.uid]);

  const saveNodesToLabeledSubgraph = useCallback(async (nodeIds, label) => {
    if (!user?.uid || !subgraphSession.active) return;
    const targetNodeIds = [...new Set((nodeIds || []).filter(Boolean))];
    if (targetNodeIds.length === 0) {
      setStatus("Select at least one node to add to subgraph.");
      return;
    }
    const subgraphNode = await ensureSubgraphNodeByLabel(label);
    if (!subgraphNode) {
      setStatus("Enter a subgraph label.");
      return;
    }
    const existingMemberships = new Set(
      edges
        .filter((edge) => edge.type === "IN_SUBGRAPH" && edge.to?.nodeId === subgraphNode.id)
        .map((edge) => edge.from?.nodeId)
        .filter(Boolean)
    );
    const toCreate = targetNodeIds.filter((nodeId) => !existingMemberships.has(nodeId));
    await Promise.all(toCreate.map((nodeId) => createEdge(DEFAULT_GRAPH_ID, {
      type: "IN_SUBGRAPH",
      from: { nodeId },
      to: { nodeId: subgraphNode.id },
      props: {},
      ownerUid: user.uid,
    })));
    setActiveSubgraphId(subgraphNode.id);
    setStatus(`Saved ${targetNodeIds.length} node(s) to subgraph "${subgraphNode.label || label}".`);
  }, [user?.uid, subgraphSession.active, ensureSubgraphNodeByLabel, edges]);

  const openSavedSubgraph = useCallback((subgraphNodeId) => {
    const memberIds = getSubgraphMemberIds(subgraphNodeId);
    const subgraphNode = nodes.find((node) => node.id === subgraphNodeId);
    setSubgraphSession({
      active: true,
      nodeIds: memberIds,
    });
    setSubgraphSelectionNodeIds(memberIds);
    setSubgraphLabelDraft(subgraphNode?.label || "");
    setActiveSubgraphId(subgraphNodeId);
    setSubgraphReferenceTargetId("");
    setSelectedNodeId(memberIds[0] || "");
    setSelectedEdgeId("");
    setSubgraphNodePositions({});
    setStatus(`Opened saved subgraph with ${memberIds.length} node(s).`);
  }, [getSubgraphMemberIds, nodes]);

  const deleteSavedSubgraph = useCallback(async (subgraphNodeId) => {
    if (!user?.uid || !subgraphNodeId) return;
    await deleteNode(DEFAULT_GRAPH_ID, subgraphNodeId, user.uid);
    if (activeSubgraphId === subgraphNodeId) {
      setActiveSubgraphId("");
      setSubgraphLabelDraft("");
      setSubgraphReferenceTargetId("");
      setSubgraphSession((prev) => ({ ...prev, nodeIds: [] }));
      setSubgraphSelectionNodeIds([]);
    }
    setStatus("Subgraph deleted. Member nodes were not deleted.");
  }, [user?.uid, activeSubgraphId]);

  const linkSubgraphToSubgraph = useCallback(async () => {
    if (!user?.uid || !subgraphSession.active || !subgraphReferenceTargetId) return;
    let sourceSubgraphId = activeSubgraphId;
    if (!sourceSubgraphId) {
      const ensured = await ensureSubgraphNodeByLabel(subgraphLabelDraft);
      if (!ensured?.id) {
        setStatus("Enter or open a source subgraph label first.");
        return;
      }
      sourceSubgraphId = ensured.id;
      setActiveSubgraphId(ensured.id);
    }
    if (sourceSubgraphId === subgraphReferenceTargetId) {
      setStatus("Choose a different subgraph to reference.");
      return;
    }
    const fromNodeId = subgraphReferenceDirection === "out" ? sourceSubgraphId : subgraphReferenceTargetId;
    const toNodeId = subgraphReferenceDirection === "out" ? subgraphReferenceTargetId : sourceSubgraphId;
    const exists = edges.some(
      (edge) => edge.type === "REF_SUBGRAPH" && edge.from?.nodeId === fromNodeId && edge.to?.nodeId === toNodeId
    );
    if (exists) {
      setStatus("Subgraph reference already exists.");
      return;
    }
    await createEdge(DEFAULT_GRAPH_ID, {
      type: "REF_SUBGRAPH",
      from: { nodeId: fromNodeId },
      to: { nodeId: toNodeId },
      props: {},
      ownerUid: user.uid,
    });
    setStatus("Subgraph reference created.");
  }, [
    user?.uid,
    subgraphSession.active,
    subgraphReferenceTargetId,
    activeSubgraphId,
    ensureSubgraphNodeByLabel,
    subgraphLabelDraft,
    subgraphReferenceDirection,
    edges,
  ]);

  const createNodeInSubgraphSession = async (overrides = {}) => {
    if (!user?.uid || !subgraphSession.active) return;
    const label = String(overrides.label || subgraphNodeQuery || "").trim() || "Subgraph Node";
    const ref = await createNode(DEFAULT_GRAPH_ID, {
      label,
      props: {},
      ui: overrides.ui || { x: 520, y: 310 },
      ownerUid: user.uid,
    });
    const sourceNodeId = selectedNode?.id && subgraphSession.nodeIds.includes(selectedNode.id)
      ? selectedNode.id
      : "";
    if (sourceNodeId) {
      await connectNodeInSubgraphSession(ref.id, sourceNodeId);
      setSubgraphNodeQuery("");
      return;
    }
    addNodeToSubgraphSession(ref.id);
    setSubgraphNodeQuery("");
    setStatus("Node created in subgraph session.");
  };

  const addOrConnectExistingNodeInSubgraphSession = async (targetNodeId) => {
    if (!subgraphSession.active || !targetNodeId) return;
    const sourceNodeId = selectedNode?.id && subgraphSession.nodeIds.includes(selectedNode.id)
      ? selectedNode.id
      : "";
    if (sourceNodeId) {
      await connectNodeInSubgraphSession(targetNodeId, sourceNodeId);
      setSelectedEdgeId("");
      setSelectedNodeId(targetNodeId);
      setSubgraphNodeQuery("");
      return;
    }
    addNodeToSubgraphSession(targetNodeId);
    setSubgraphNodeQuery("");
  };
  const handleFlowNodeClick = (event, node) => {
    if (subgraphSession.active && event?.shiftKey) {
      setSelectedEdgeId("");
      setSelectedNodeId(node.id);
      if (!subgraphLinkSourceId) {
        setSubgraphLinkSourceId(node.id);
        setStatus("Link source selected. Shift+click another node to connect.");
        return;
      }
      if (subgraphLinkSourceId === node.id) {
        setSubgraphLinkSourceId("");
        setStatus("Link source cleared.");
        return;
      }
      void runAction("Connect nodes", () => connectTwoSubgraphNodes(subgraphLinkSourceId, node.id));
      setSubgraphLinkSourceId(node.id);
      return;
    }
    setSelectedEdgeId("");
    setSelectedNodeId(node.id);
    if (subgraphSession.active) {
      setSubgraphSelectionNodeIds([node.id]);
    }
  };

  const handleSelectionChange = useCallback(({ nodes: selectedNodes }) => {
    if (!subgraphSession.active) return;
    setSubgraphSelectionNodeIds((selectedNodes || []).map((node) => node.id));
  }, [subgraphSession.active]);

  const attachPropertyToType = async (propertyDefId) => {
    if (!selectedNode || !isSelectedTypeNode || !propertyDefId || !user?.uid) return;
    const exists = typePropertyEdges.some((edge) => edge.to?.nodeId === propertyDefId);
    if (exists) return;
    await createEdge(DEFAULT_GRAPH_ID, {
      type: "HAS_PROPERTY",
      from: { nodeId: selectedNode.id },
      to: { nodeId: propertyDefId },
      props: { required: false },
      ownerUid: user.uid,
    });
    setStatus("Property attached to type.");
  };

  const savePropertyDefinition = async () => {
    if (!selectedNode || !isSelectedPropertyNode) return;
    const constraints = {};
    if (propertySchemaDraft.constraintPattern) constraints.pattern = propertySchemaDraft.constraintPattern;
    if (propertySchemaDraft.constraintMin !== "") constraints.min = Number(propertySchemaDraft.constraintMin);
    if (propertySchemaDraft.constraintMax !== "") constraints.max = Number(propertySchemaDraft.constraintMax);
    if (propertySchemaDraft.constraintEnum.trim()) {
      constraints.enum = propertySchemaDraft.constraintEnum.split(",").map((item) => item.trim()).filter(Boolean);
    }
    await updateNode(DEFAULT_GRAPH_ID, selectedNode.id, {
      label: propertySchemaDraft.label || selectedNode.label || "Property",
      props: {
        kind: "Property",
        key: propertySchemaDraft.key.trim(),
        valueType: propertySchemaDraft.valueType,
        unit: propertySchemaDraft.unit.trim() || null,
        cardinality: propertySchemaDraft.cardinality.trim() || "0..1",
        description: propertySchemaDraft.description || "",
        constraints,
        privacyDefault: propertySchemaDraft.privacyDefault || "private",
      },
    });
    setStatus("Property definition saved.");
  };

  const saveNode = async () => {
    if (!selectedNode) return;
    if (!parsedNodeProps.ok) {
      setStatus(parsedNodeProps.error);
      return;
    }
    if (nodeTemporalError) {
      setStatus(nodeTemporalError);
      return;
    }
    await updateNode(DEFAULT_GRAPH_ID, selectedNode.id, {
      label: nodeLabelDraft,
      props: {
        ...(selectedNode.props?.kind ? { kind: selectedNode.props.kind } : {}),
        ...parsedNodeProps.value,
      },
      occurredAt: fromDateTimeLocalValue(nodeTimeDraft.occurredAt),
      validFrom: fromDateTimeLocalValue(nodeTimeDraft.validFrom),
      validTo: fromDateTimeLocalValue(nodeTimeDraft.validTo),
    });
    setStatus("Node saved.");
  };

  const updateNodePropDraft = useCallback((key, rawValue) => {
    if (!parsedNodeProps.ok) return;
    const current = parsedNodeProps.value || {};
    const next = {
      ...current,
      [key]: coercePropValue(rawValue, current[key]),
    };
    setNodePropsText(JSON.stringify(next, null, 2));
  }, [parsedNodeProps]);

  const saveEdge = async () => {
    if (!selectedEdge) return;
    const parsed = validateJSON(edgePropsText);
    if (!parsed.ok) {
      setStatus(parsed.error);
      return;
    }
    if (edgeTemporalError) {
      setStatus(edgeTemporalError);
      return;
    }
    await updateEdge(DEFAULT_GRAPH_ID, selectedEdge.id, {
      type: selectedEdge.type || "RELATES_TO",
      from: selectedEdge.from,
      to: selectedEdge.to,
      props: parsed.value,
      occurredAt: fromDateTimeLocalValue(edgeTimeDraft.occurredAt),
      validFrom: fromDateTimeLocalValue(edgeTimeDraft.validFrom),
      validTo: fromDateTimeLocalValue(edgeTimeDraft.validTo),
    });
    setStatus("Edge saved.");
  };

  const addTypeToNode = async (typeNodeId) => {
    if (!selectedNode || !typeNodeId || !user?.uid) return;
    const existing = selectedNodeDirectTypeEdges.find((edge) => edge.to.nodeId === typeNodeId);
    if (existing) return;
    await createEdge(DEFAULT_GRAPH_ID, {
      type: "HAS_TYPE",
      from: { nodeId: selectedNode.id },
      to: { nodeId: typeNodeId },
      props: {},
      ownerUid: user.uid,
    });
    setStatus("Type added.");
  };

  const addParentType = async (parentTypeId) => {
    if (!selectedNode || selectedNode.props?.kind !== "Type" || !parentTypeId || !user?.uid) return;
    const existing = selectedTypeParents.find((edge) => edge.to.nodeId === parentTypeId);
    if (existing || parentTypeId === selectedNode.id) return;
    await createEdge(DEFAULT_GRAPH_ID, {
      type: "IS_A",
      from: { nodeId: selectedNode.id },
      to: { nodeId: parentTypeId },
      props: {},
      ownerUid: user.uid,
    });
    setStatus("Parent type linked.");
  };

  const addRefersTo = async (targetNodeId) => {
    if (!selectedNode || !targetNodeId || !user?.uid) return;
    const exists = selectedNodeRefersTo.some((edge) => edge.to.nodeId === targetNodeId);
    if (exists || targetNodeId === selectedNode.id) return;
    await createEdge(DEFAULT_GRAPH_ID, {
      type: "REFERS_TO",
      from: { nodeId: selectedNode.id },
      to: { nodeId: targetNodeId },
      props: {},
      ownerUid: user.uid,
    });
    setStatus("REFERS_TO link added.");
  };

  const isToloNote = Boolean(selectedNode
    && selectedNodeInferredTypeLabels.includes("Note")
    && selectedNodeInferredTypeLabels.includes("TOLOPOSOGO"));

  const deleteSelectedNode = async () => {
    if (!selectedNode || !user?.uid) return;
    await deleteNode(DEFAULT_GRAPH_ID, selectedNode.id, user.uid);
    setSelectedNodeId("");
  };

  const selectedNodeValidAsOf = useMemo(
    () => {
      if (!asOfEnabled || !selectedNode) return true;
      if (filterMode === "range") {
        return isValidInRange(selectedNode, rangeStart, rangeEnd) || isOccurredInRange(selectedNode, rangeStart, rangeEnd);
      }
      return isValidAt(selectedNode, asOfDate);
    },
    [asOfEnabled, selectedNode, filterMode, asOfDate, rangeStart, rangeEnd]
  );

  const selectedEdgeValidAsOf = useMemo(
    () => {
      if (!asOfEnabled || !selectedEdge) return true;
      if (filterMode === "range") {
        return isValidInRange(selectedEdge, rangeStart, rangeEnd) || isOccurredInRange(selectedEdge, rangeStart, rangeEnd);
      }
      return isValidAt(selectedEdge, asOfDate);
    },
    [asOfEnabled, selectedEdge, filterMode, asOfDate, rangeStart, rangeEnd]
  );
  const birthdateDraftValue = useMemo(() => {
    const propDefId = propertyKeyToDefId.birthdate;
    if (!propDefId) return "";
    const draft = propertyDrafts[propDefId];
    if (!draft) return "";
    return String(draft.value || "");
  }, [propertyKeyToDefId, propertyDrafts]);
  const derivedAge = useMemo(
    () => computeAgeFromBirthdate(
      birthdateDraftValue,
      asOfEnabled ? (filterMode === "range" ? rangeStart : asOfDate) : new Date()
    ),
    [birthdateDraftValue, asOfEnabled, filterMode, asOfDate, rangeStart]
  );
  const derivedNextBirthday = useMemo(
    () => computeNextBirthday(
      birthdateDraftValue,
      asOfEnabled ? (filterMode === "range" ? rangeStart : asOfDate) : new Date()
    ),
    [birthdateDraftValue, asOfEnabled, filterMode, asOfDate, rangeStart]
  );

  const jumpAsOfToSelectedNodeTime = () => {
    if (!selectedNode) return;
    const candidate = normalizeTimestamp(selectedNode.occurredAt) || normalizeTimestamp(selectedNode.validFrom);
    if (!candidate) return;
    setAsOfEnabled(true);
    setLiveEnabled(false);
    setAsOfDate(candidate);
  };

  const jumpAsOfToSelectedEdgeTime = () => {
    if (!selectedEdge) return;
    const candidate = normalizeTimestamp(selectedEdge.occurredAt) || normalizeTimestamp(selectedEdge.validFrom);
    if (!candidate) return;
    setAsOfEnabled(true);
    setLiveEnabled(false);
    setAsOfDate(candidate);
  };

  const commitPropertyValue = useCallback(async (propertyDefId, nextDraft) => {
    if (!selectedNode || !user?.uid) return;
    const slot = propertySchemaSlots.find((item) => item.propertyDefId === propertyDefId);
    if (!slot) return;
    const normalized = normalizeValue(slot.propertyDef.props?.valueType, nextDraft.value);
    const validation = validateValue(slot.propertyDef.props?.valueType, normalized, slot.propertyDef.props?.constraints || {});
    if (!validation.ok) return;
    if (normalized == null || normalized === "") {
      await removeValueEdge(DEFAULT_GRAPH_ID, user.uid, selectedNode.id, propertyDefId);
      setStatus("Value removed.");
      return;
    }
    await upsertValueEdge(
      DEFAULT_GRAPH_ID,
      user.uid,
      selectedNode.id,
      propertyDefId,
      normalized,
      nextDraft.privacy
    );
  }, [selectedNode, user?.uid, propertySchemaSlots]);

  const queuePropertySave = useCallback((propertyDefId, nextDraft) => {
    const existing = propertySaveTimersRef.current[propertyDefId];
    if (existing) {
      clearTimeout(existing);
    }
    propertySaveTimersRef.current[propertyDefId] = setTimeout(() => {
      commitPropertyValue(propertyDefId, nextDraft).catch((error) => {
        setStatus(`Save property failed: ${error?.message || "Unknown error"}`);
      });
    }, 450);
  }, [commitPropertyValue]);

  const setPropertyDraftField = (propertyDefId, patch) => {
    const slot = propertySchemaSlots.find((item) => item.propertyDefId === propertyDefId);
    if (!slot) return;
    setPropertyDrafts((prev) => {
      const current = prev[propertyDefId] || { value: "", privacy: slot.privacyDefault || "private", error: "" };
      const next = {
        ...current,
        ...patch,
      };
      const normalized = normalizeValue(slot.propertyDef.props?.valueType, next.value);
      const validation = validateValue(slot.propertyDef.props?.valueType, normalized, slot.propertyDef.props?.constraints || {});
      next.error = validation.ok ? "" : validation.error;
      if (validation.ok) {
        queuePropertySave(propertyDefId, next);
      }
      return {
        ...prev,
        [propertyDefId]: next,
      };
    });
  };

  const removePropertyValue = async (propertyDefId) => {
    if (!selectedNode || !user?.uid) return;
    await removeValueEdge(DEFAULT_GRAPH_ID, user.uid, selectedNode.id, propertyDefId);
    setPropertyDrafts((prev) => ({
      ...prev,
      [propertyDefId]: {
        ...(prev[propertyDefId] || {}),
        value: "",
      },
    }));
    setStatus("Property value removed.");
  };

  const focusNode = useCallback((nodeId) => {
    if (!nodeId) return;
    setSelectedEdgeId("");
    setSelectedNodeId(nodeId);
  }, []);

  const addLocationToSelectedNode = async ({ edgeType, place }) => {
    if (!selectedNode || !user?.uid || !place?.placeId) return;
    const placeTypeNode = await ensurePlaceTypeNode(DEFAULT_GRAPH_ID, user.uid);
    let placeNode = await findPlaceNodeByPlaceId(DEFAULT_GRAPH_ID, user.uid, place.placeId);

    if (!placeNode) {
      placeNode = await createPlaceNodeFromPlaceResult(DEFAULT_GRAPH_ID, user.uid, place);
    }

    await addHasTypeEdge(DEFAULT_GRAPH_ID, user.uid, placeNode.id, placeTypeNode.id);
    await addLocationEdge(DEFAULT_GRAPH_ID, user.uid, selectedNode.id, placeNode.id, edgeType, {});
    setStatus("Location added.");
  };

  const uploadImageForSelectedNode = async (file) => {
    if (!user?.uid || !selectedNode) return;
    if (!file) throw new Error("No image selected.");
    if (!file.type?.startsWith("image/")) throw new Error("Please choose an image file.");
    if (file.size > 10 * 1024 * 1024) throw new Error("Image is too large (max 10MB).");

    setUploadingNodeImage(true);
    try {
      const safeName = String(file.name || "image").replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `quilt_node_images/${user.uid}/${selectedNode.id}/${Date.now()}_${safeName}`;
      const uploadRef = storageRef(storage, path);
      await uploadBytes(uploadRef, file, {
        contentType: file.type || "image/png",
        cacheControl: "public,max-age=3600",
      });
      const url = await getDownloadURL(uploadRef);
      const nextImages = [
        ...selectedNodeImages,
        {
          id: path,
          name: file.name || "image",
          url,
          path,
          contentType: file.type || "",
          size: file.size || 0,
          createdAt: new Date().toISOString(),
        },
      ];
      await updateNode(DEFAULT_GRAPH_ID, selectedNode.id, {
        props: {
          ...(selectedNode.props || {}),
          images: nextImages.map((img) => ({
            name: img.name || "",
            url: img.url,
            path: img.path || "",
            contentType: img.contentType || "",
            size: img.size || 0,
            createdAt: img.createdAt || "",
          })),
        },
      });
      setStatus("Image added to node.");
    } finally {
      setUploadingNodeImage(false);
      if (nodeImageInputRef.current) nodeImageInputRef.current.value = "";
    }
  };

  const removeImageFromSelectedNode = async (image) => {
    if (!selectedNode) return;
    const nextImages = selectedNodeImages.filter((item) => item.id !== image.id);
    await updateNode(DEFAULT_GRAPH_ID, selectedNode.id, {
      props: {
        ...(selectedNode.props || {}),
        images: nextImages.map((img) => ({
          name: img.name || "",
          url: img.url,
          path: img.path || "",
          contentType: img.contentType || "",
          size: img.size || 0,
          createdAt: img.createdAt || "",
        })),
      },
    });
    if (image.path) {
      try {
        await deleteObject(storageRef(storage, image.path));
      } catch (error) {
        if (error?.code !== "storage/object-not-found") throw error;
      }
    }
    setStatus("Image removed from node.");
  };

  const updateLocationEdgeProps = async (edgeId, patchProps) => {
    const edge = allEdgeById.get(edgeId);
    if (!edge) return;
    await updateEdge(DEFAULT_GRAPH_ID, edgeId, {
      props: {
        ...(edge.props || {}),
        ...patchProps,
      },
    });
  };

  const flowNodes = useMemo(() => (
    visibleNodes.map((node, idx) => {
      const isType = node.props?.kind === "Type";
      return {
        id: node.id,
        data: { label: node.label || node.id },
        selected: subgraphSession.active && subgraphSelectionNodeIds.includes(node.id),
        position: subgraphSession.active
          ? (subgraphNodePositions[node.id] || subgraphPositionById.get(node.id) || getNodePosition(node, idx))
          : layoutMode === "exploded"
          ? (explodedPositionById.get(node.id) || getNodePosition(node, idx))
          : getNodePosition(node, idx),
        style: {
          border: selectedNodeId === node.id ? "2px solid #dc2626" : "1px solid #94a3b8",
          borderRadius: 12,
          padding: "8px 10px",
          background: isType ? "#fff7ed" : "#f8fafc",
          fontWeight: 600,
          minWidth: 90,
          color: "#0f172a",
        },
      };
    })
  ), [
    visibleNodes,
    selectedNodeId,
    layoutMode,
    explodedPositionById,
    subgraphSession.active,
    subgraphPositionById,
    subgraphNodePositions,
    subgraphSelectionNodeIds,
  ]);

  const flowEdges = useMemo(() => (
    visibleEdges.map((edge) => {
      const fromId = edge?.from?.nodeId;
      const toId = edge?.to?.nodeId;
      if (!fromId || !toId) return null;
      const selectedIsSource = selectedNodeId && fromId === selectedNodeId;
      const selectedIsTarget = selectedNodeId && toId === selectedNodeId;
      const touchesSelectedNode = Boolean(selectedIsSource || selectedIsTarget);
      let stroke = "#64748b";
      let strokeWidth = 2;

      if (touchesSelectedNode) {
        stroke = selectedIsSource ? "#2563eb" : "#16a34a";
        strokeWidth = 3.5;
      }
      if (selectedEdgeId === edge.id) {
        stroke = "#dc2626";
        strokeWidth = 4;
      }

      return {
        id: edge.id,
        source: fromId,
        target: toId,
        label: edge.type,
        style: {
          stroke,
          strokeWidth,
        },
        labelStyle: { fontSize: 11, fill: "#334155" },
        markerEnd: { type: "arrowclosed" },
      };
    })
  ).filter(Boolean), [visibleEdges, selectedEdgeId, selectedNodeId]);

  const handleNodeDragStop = async (_event, node) => {
    if (subgraphSession.active) {
      setSubgraphNodePositions((prev) => ({
        ...prev,
        [node.id]: {
          x: node.position.x,
          y: node.position.y,
        },
      }));
      return;
    }
    if (layoutMode === "exploded") return;
    await updateNode(DEFAULT_GRAPH_ID, node.id, {
      ui: {
        x: node.position.x,
        y: node.position.y,
      },
    });
  };

  if (!user) {
    return <Box sx={{ p: 3 }}><Typography>Sign in to use Quilt.</Typography></Box>;
  }

  return (
    <Box sx={{ minHeight: "100vh", p: 2, background: "#f4f7fb" }}>
      <Paper sx={{ p: 1.2, mb: 1.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" fontWeight={700}>Attention Tools</Typography>
          {ATTENTION_TOOLS.map((tool) => (
            <Chip
              key={tool.id}
              label={tool.label}
              color={activeAttentionToolId === tool.id ? "primary" : "default"}
              variant={activeAttentionToolId === tool.id ? "filled" : "outlined"}
              onClick={() => setActiveAttentionToolId((prev) => (prev === tool.id ? "" : tool.id))}
            />
          ))}
          <Button size="small" onClick={() => setActiveAttentionToolId("")}>Clear</Button>
          {!!activeAttentionTool && (
            <Typography variant="caption" color="text.secondary">
              {activeAttentionTool.title}: {activeAttentionTool.prompt}
            </Typography>
          )}
        </Stack>
      </Paper>
      <Stack direction="row" spacing={2} alignItems="stretch">
        <Paper sx={{ width: 300, p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="h6" fontWeight={800}>Quilt Graph</Typography>
            <AsOfControl />
            <TextField
              size="small"
              label={subgraphSession.active ? "Search master graph to add" : "Search nodes"}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {subgraphSession.active && searchLower && (
              <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                {subgraphMasterSearchResults.map((node) => (
                  <Chip
                    key={node.id}
                    label={node.label || node.id}
                    onClick={() => void runAction("Add/connect node from master search", () => addOrConnectExistingNodeInSubgraphSession(node.id))}
                  />
                ))}
                {subgraphMasterSearchResults.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No matching nodes in master graph.</Typography>
                )}
              </Stack>
            )}
            <TextField
              select
              size="small"
              label="View"
              value={view}
              onChange={(event) => setView(event.target.value)}
            >
              {VIEWS.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Type picker search"
              value={typeSearch}
              onChange={(event) => setTypeSearch(event.target.value)}
            />
            <TextField
              select
              size="small"
              label="Type filter"
              value={selectedTypeFilterId}
              onChange={(event) => setSelectedTypeFilterId(event.target.value)}
            >
              <MenuItem value="">No type filter</MenuItem>
              {typeFilterOptions.map((typeNode) => (
                <MenuItem key={typeNode.id} value={typeNode.id}>{typeNode.label}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={<Switch checked={showPlaces} onChange={(event) => setShowPlaces(event.target.checked)} />}
              label="Show Places"
            />
            <Divider />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => void runAction("Create node", () => createBlankNode(false))}>New Node</Button>
              <Button variant="outlined" onClick={() => void runAction("Create type node", () => createBlankNode(true))}>New Type</Button>
            </Stack>
            <Button
              variant={subgraphSession.active ? "contained" : "outlined"}
              color={subgraphSession.active ? "warning" : "primary"}
              onClick={() => void runAction(
                subgraphSession.active ? "Finish subgraph session" : "Start subgraph session",
                () => {
                  if (subgraphSession.active) {
                    finishSubgraphSession();
                    return;
                  }
                  startSubgraphSession();
                }
              )}
            >
              {subgraphSession.active ? "Finish subgraph session" : "New subgraph session"}
            </Button>
            <Button
              variant={layoutMode === "exploded" ? "contained" : "outlined"}
              onClick={() => setLayoutMode((prev) => (prev === "exploded" ? "saved" : "exploded"))}
              disabled={subgraphSession.active}
            >
              {layoutMode === "exploded" ? "Use saved layout" : "Exploded layout"}
            </Button>
            <Button variant="outlined" onClick={() => void runAction("Create property node", createPropertyNode)}>New Property Node</Button>
            <Button variant="outlined" onClick={() => void runAction("Create event node", createEventNode)}>New Event Node</Button>
            <Button variant="outlined" onClick={() => void runAction("Create edge", createBlankEdge)} disabled={timelineNodes.length < 2}>New Edge</Button>
            <Button variant="outlined" onClick={() => void runAction("Create state edge", createStateEdge)} disabled={timelineNodes.length < 2}>New State Edge</Button>
            <Button
              variant="outlined"
              onClick={() => void runAction("Create base type set", () => createBaseTypeSet(DEFAULT_GRAPH_ID, user.uid))}
            >
              Create base type set
            </Button>
            <Button
              variant="outlined"
              onClick={() => void runAction("Bootstrap base schema", () => ensureBaseSchema(DEFAULT_GRAPH_ID, user.uid))}
            >
              Bootstrap base schema
            </Button>
            <Typography variant="caption" color="text.secondary">
              {visibleNodes.length} nodes • {visibleEdges.length} edges
            </Typography>
          </Stack>
        </Paper>

        <Paper
          sx={{
            flex: 1,
            borderRadius: 2,
            minHeight: "82vh",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {selectedEdge && (
            <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 12 }}>
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={() => void runAction("Delete selected edge", async () => {
                  await deleteEdge(DEFAULT_GRAPH_ID, selectedEdge.id);
                  setSelectedEdgeId("");
                })}
              >
                Delete selected edge
              </Button>
            </Box>
          )}
          {(subgraphSession.active ? subgraphViewMode === "Map" : view === "Map") ? (
            <LocationMapCanvas
              nodes={visibleNodes}
              edges={visibleEdges}
              placeNodeIds={placeNodeIds}
              selectedNodeId={selectedNodeId}
              onSelectNode={(nodeId) => {
                setSelectedEdgeId("");
                setSelectedNodeId(nodeId);
              }}
            />
          ) : (
            <Box sx={{ width: "100%", height: "82vh" }}>
              <ReactFlow
                nodes={flowNodes}
                edges={flowEdges}
                fitView
                selectionOnDrag
                onSelectionChange={handleSelectionChange}
                onNodeClick={handleFlowNodeClick}
                onEdgeClick={(_event, edge) => {
                  setSelectedNodeId("");
                  setSelectedEdgeId(edge.id);
                }}
                onPaneClick={(event) => {
                  if (subgraphSession.active && event?.detail >= 2) {
                    void runAction("Create node in subgraph session", () => createNodeInSubgraphSession({ label: "Subgraph Node" }));
                    return;
                  }
                  setSelectedNodeId("");
                  setSelectedEdgeId("");
                  if (subgraphSession.active) {
                    setSubgraphLinkSourceId("");
                    setSubgraphSelectionNodeIds([]);
                  }
                }}
                onNodeDragStop={handleNodeDragStop}
              >
                <MiniMap />
                <Controls />
                <Background gap={16} size={0.8} />
              </ReactFlow>
            </Box>
          )}
        </Paper>

        <Paper sx={{ width: 360, p: 2, borderRadius: 2, maxHeight: "82vh", overflow: "auto" }}>
          <Stack spacing={1.5}>
            <Typography variant="h6" fontWeight={800}>Inspector</Typography>
            {!!status && <Alert severity="info">{status}</Alert>}
            {!!activeAttentionTool && (
              <Alert severity="info">
                <Typography variant="subtitle2" fontWeight={700}>{activeAttentionTool.title}</Typography>
                <Typography variant="body2">{activeAttentionTool.prompt}</Typography>
              </Alert>
            )}
            {(loadingNodes || loadingEdges) && <Typography variant="body2">Loading graph…</Typography>}
            {!!nodesError && <Alert severity="error">{nodesError}</Alert>}
            {!!edgesError && <Alert severity="error">{edgesError}</Alert>}

            {selectedNode && (
              <>
                <Typography variant="subtitle1" fontWeight={700}>Node</Typography>
                {asOfEnabled && !selectedNodeValidAsOf && (
                  <Alert
                    severity="warning"
                    action={(
                      <Button size="small" onClick={jumpAsOfToSelectedNodeTime}>
                        Jump as-of
                      </Button>
                    )}
                  >
                    This item is not valid at the current as-of time; adjust time or disable as-of.
                  </Alert>
                )}
                <TextField
                  size="small"
                  label="Label"
                  value={nodeLabelDraft}
                  onChange={(event) => setNodeLabelDraft(event.target.value)}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    type="datetime-local"
                    label="Occurred at"
                    value={nodeTimeDraft.occurredAt}
                    onChange={(event) => setNodeTimeDraft((prev) => ({ ...prev, occurredAt: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    type="datetime-local"
                    label="Valid from"
                    value={nodeTimeDraft.validFrom}
                    onChange={(event) => setNodeTimeDraft((prev) => ({ ...prev, validFrom: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="datetime-local"
                    label="Valid to"
                    value={nodeTimeDraft.validTo}
                    onChange={(event) => setNodeTimeDraft((prev) => ({ ...prev, validTo: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
                {!!nodeTemporalError && <Alert severity="error">{nodeTemporalError}</Alert>}
                <Typography variant="subtitle2" fontWeight={700}>Prop fields</Typography>
                {!parsedNodeProps.ok && (
                  <Alert severity="warning">Fix invalid Props JSON to edit generated fields.</Alert>
                )}
                {parsedNodeProps.ok && Object.entries(parsedNodeProps.value || {}).map(([key, value]) => (
                  <TextField
                    key={key}
                    size="small"
                    fullWidth
                    type={typeof value === "number" ? "number" : "text"}
                    select={typeof value === "boolean"}
                    multiline={value !== null && typeof value === "object"}
                    minRows={value !== null && typeof value === "object" ? 2 : undefined}
                    label={key}
                    value={typeof value === "boolean" ? String(value) : stringifyPropValue(value)}
                    onChange={(event) => updateNodePropDraft(key, event.target.value)}
                  >
                    {typeof value === "boolean" && (
                      [
                        <MenuItem key="true" value="true">true</MenuItem>,
                        <MenuItem key="false" value="false">false</MenuItem>,
                      ]
                    )}
                  </TextField>
                ))}
                {parsedNodeProps.ok && Object.keys(parsedNodeProps.value || {}).length === 0 && (
                  <Typography variant="caption" color="text.secondary">No props on this node.</Typography>
                )}
                <TextField
                  multiline
                  minRows={7}
                  label="Props JSON"
                  value={nodePropsText}
                  onChange={(event) => setNodePropsText(event.target.value)}
                  error={!parsedNodeProps.ok}
                  helperText={parsedNodeProps.ok ? "" : parsedNodeProps.error}
                />
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={() => void runAction("Save node", saveNode)}>Save node</Button>
                  <Button color="error" variant="outlined" onClick={() => void runAction("Delete node", deleteSelectedNode)}>Delete node</Button>
                </Stack>
                <Button onClick={() => void copyText(buildNodeUri(selectedNode.id))}>Copy Quilt Link</Button>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>Images</Typography>
                <input
                  ref={nodeImageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    void runAction("Upload node image", () => uploadImageForSelectedNode(file));
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => nodeImageInputRef.current?.click()}
                  disabled={uploadingNodeImage}
                >
                  {uploadingNodeImage ? "Uploading..." : "Add image"}
                </Button>
                {selectedNodeImages.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No images on this node.</Typography>
                )}
                <Stack spacing={1}>
                  {selectedNodeImages.map((image) => (
                    <Stack key={image.id} spacing={0.5} sx={{ p: 1, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                      <Box
                        component="img"
                        src={image.url}
                        alt={image.name || "Node image"}
                        sx={{
                          width: "100%",
                          maxHeight: 180,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "1px solid #e2e8f0",
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {image.name || "Image"}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => window.open(image.url, "_blank", "noopener,noreferrer")}>
                          Open
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => void runAction("Remove node image", () => removeImageFromSelectedNode(image))}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>

                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>Types</Typography>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  {selectedNodeDirectTypeEdges.map((edge) => (
                    <Chip
                      key={edge.id}
                      label={typeLabelLookup.get(edge.to.nodeId) || edge.to.nodeId}
                      onDelete={() => void runAction("Remove node type", () => deleteEdge(DEFAULT_GRAPH_ID, edge.id))}
                    />
                  ))}
                </Stack>
                <TextField
                  size="small"
                  label="Search type to add"
                  value={typeAddSearch}
                  onChange={(event) => setTypeAddSearch(event.target.value)}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {addTypeOptions.slice(0, 12).map((typeNode) => (
                    <Chip
                      key={typeNode.id}
                      label={typeNode.label}
                      onClick={() => void addTypeToNode(typeNode.id)}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Inferred: {selectedNodeInferredTypeLabels.join(", ") || "None"}
                </Typography>

                {isSelectedEntityNode && (
                  <>
                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>Properties</Typography>
                    {propertySchemaSlots.length === 0 && (
                      <Typography variant="caption" color="text.secondary">No inherited properties found for this entity.</Typography>
                    )}
                    <Stack spacing={1}>
                      {propertySchemaSlots.map((slot) => {
                        const draft = propertyDrafts[slot.propertyDefId] || {};
                        const valueType = slot.propertyDef.props?.valueType || "String";
                        const cardinality = String(slot.propertyDef.props?.cardinality || "0..1");
                        const canRemoveValue = !cardinality.startsWith("1..");
                        const providedByLabels = (slot.providedByTypes || [])
                          .map((typeId) => allNodeById.get(typeId)?.label || typeId)
                          .join(", ");

                        return (
                          <Stack key={slot.propertyDefId} spacing={0.8} sx={{ p: 1, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                              <Typography variant="body2" fontWeight={700}>
                                {slot.propertyDef.label || slot.propertyDef.props?.key}
                              </Typography>
                              <Chip size="small" label={slot.propertyDef.props?.key || slot.propertyDefId} />
                              <Chip size="small" label={valueType} />
                              {slot.required && <Chip size="small" color="warning" label="Required" />}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              Provided by: {providedByLabels || "Unknown"} {slot.propertyDef.props?.unit ? `• Unit: ${slot.propertyDef.props.unit}` : ""} • Cardinality: {cardinality}
                            </Typography>

                            {valueType === "Boolean" ? (
                              <FormControlLabel
                                control={(
                                  <Switch
                                    checked={Boolean(draft.value)}
                                    onChange={(event) => setPropertyDraftField(slot.propertyDefId, { value: event.target.checked })}
                                  />
                                )}
                                label={slot.propertyDef.label || slot.propertyDef.props?.key}
                              />
                            ) : (
                              <TextField
                                size="small"
                                type={valueType === "Number" ? "number" : (valueType === "Date" ? "date" : (valueType === "Email" ? "email" : (valueType === "PhoneE164" ? "tel" : "text")))}
                                label={slot.propertyDef.label || slot.propertyDef.props?.key}
                                value={draft.value ?? ""}
                                onChange={(event) => setPropertyDraftField(slot.propertyDefId, { value: event.target.value })}
                                InputLabelProps={valueType === "Date" ? { shrink: true } : undefined}
                              />
                            )}

                            <Stack direction="row" spacing={1}>
                              <TextField
                                select
                                size="small"
                                label="Privacy"
                                value={draft.privacy || slot.privacyDefault || "private"}
                                onChange={(event) => setPropertyDraftField(slot.propertyDefId, { privacy: event.target.value })}
                                fullWidth
                              >
                                {PRIVACY_OPTIONS.map((item) => (
                                  <MenuItem key={item} value={item}>{item}</MenuItem>
                                ))}
                              </TextField>
                              <Button
                                size="small"
                                color="error"
                                disabled={!canRemoveValue}
                                onClick={() => void runAction("Remove value", () => removePropertyValue(slot.propertyDefId))}
                              >
                                Remove
                              </Button>
                            </Stack>
                            {!!draft.error && <Alert severity="error">{draft.error}</Alert>}
                          </Stack>
                        );
                      })}
                    </Stack>
                    <Typography variant="subtitle2" fontWeight={700}>Derived</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Age: {derivedAge == null ? "—" : derivedAge} • Next birthday: {derivedNextBirthday || "—"}
                    </Typography>
                  </>
                )}

                {!isSelectedPlaceNode && (
                  <LocationsSection
                    selectedNode={selectedNode}
                    edges={timelineEdges}
                    nodeById={nodeById}
                    onAddLocation={(payload) => runAction("Add location", () => addLocationToSelectedNode(payload))}
                    onUpdateEdgeProps={(edgeId, patchProps) => updateLocationEdgeProps(edgeId, patchProps).catch(() => {})}
                    onRemoveEdge={(edgeId) => runAction("Remove location", () => deleteEdge(DEFAULT_GRAPH_ID, edgeId))}
                    onFocusNode={focusNode}
                  />
                )}

                {isSelectedPlaceNode && (
                  <PlaceDetailsSection
                    selectedNode={selectedNode}
                    nodes={timelineNodes}
                    edges={timelineEdges}
                    onFocusNode={focusNode}
                  />
                )}

                {selectedNode.props?.kind === "Type" && (
                  <>
                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>Type Inheritance</Typography>
                    <Typography variant="caption">Parent types</Typography>
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      {selectedTypeParents.map((edge) => (
                        <Chip
                          key={edge.id}
                          label={typeLabelLookup.get(edge.to.nodeId) || edge.to.nodeId}
                          onDelete={() => void runAction("Remove parent type", () => deleteEdge(DEFAULT_GRAPH_ID, edge.id))}
                        />
                      ))}
                    </Stack>
                    <Typography variant="caption">Children</Typography>
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      {selectedTypeChildren.map((edge) => (
                        <Chip
                          key={edge.id}
                          label={typeLabelLookup.get(edge.from.nodeId) || edge.from.nodeId}
                        />
                      ))}
                    </Stack>
                    <TextField
                      size="small"
                      label="Search parent type"
                      value={parentTypeSearch}
                      onChange={(event) => setParentTypeSearch(event.target.value)}
                    />
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      {parentTypeOptions.slice(0, 12).map((typeNode) => (
                        <Chip key={typeNode.id} label={`Parent: ${typeNode.label}`} onClick={() => void addParentType(typeNode.id)} />
                      ))}
                    </Stack>
                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>Properties on This Type</Typography>
                    <Stack spacing={0.8}>
                      {typePropertyEdges.map((edge) => {
                        const propertyNode = nodeById.get(edge.to?.nodeId) || allNodeById.get(edge.to?.nodeId);
                        return (
                          <Stack key={edge.id} spacing={0.6} sx={{ p: 1, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                            <Typography variant="body2" fontWeight={700}>
                              {propertyNode?.label || propertyNode?.props?.key || edge.to?.nodeId}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <FormControlLabel
                                control={(
                                  <Switch
                                    checked={edge.props?.required === true}
                                    onChange={(event) => {
                                      updateEdge(DEFAULT_GRAPH_ID, edge.id, {
                                        props: {
                                          ...(edge.props || {}),
                                          required: event.target.checked,
                                        },
                                      }).catch(() => {});
                                    }}
                                  />
                                )}
                                label="Required"
                              />
                              <TextField
                                select
                                size="small"
                                label="Privacy override"
                                value={edge.props?.privacyDefault || ""}
                                onChange={(event) => {
                                  updateEdge(DEFAULT_GRAPH_ID, edge.id, {
                                    props: {
                                      ...(edge.props || {}),
                                      privacyDefault: event.target.value || null,
                                    },
                                  }).catch(() => {});
                                }}
                                sx={{ minWidth: 140 }}
                              >
                                <MenuItem value="">None</MenuItem>
                                {PRIVACY_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                              </TextField>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => void runAction("Remove HAS_PROPERTY", () => deleteEdge(DEFAULT_GRAPH_ID, edge.id))}
                              >
                                Remove
                              </Button>
                            </Stack>
                          </Stack>
                        );
                      })}
                    </Stack>
                    <TextField
                      size="small"
                      label="Search property to attach"
                      value={typePropertySearch}
                      onChange={(event) => setTypePropertySearch(event.target.value)}
                    />
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      {filteredPropertyNodes.slice(0, 16).map((propertyNode) => (
                        <Chip
                          key={propertyNode.id}
                          label={`${propertyNode.label || propertyNode.props?.key}`}
                          onClick={() => void attachPropertyToType(propertyNode.id)}
                        />
                      ))}
                    </Stack>
                  </>
                )}

                {isSelectedPropertyNode && (
                  <>
                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>Property Definition</Typography>
                    <TextField
                      size="small"
                      label="Label"
                      value={propertySchemaDraft.label}
                      onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, label: event.target.value }))}
                    />
                    <TextField
                      size="small"
                      label="Key"
                      value={propertySchemaDraft.key}
                      onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, key: event.target.value }))}
                      helperText="Stable key (for example: birthdate, email, massKg)"
                    />
                    <TextField
                      select
                      size="small"
                      label="Value type"
                      value={propertySchemaDraft.valueType}
                      onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, valueType: event.target.value }))}
                    >
                      {VALUE_TYPES.map((valueType) => (
                        <MenuItem key={valueType} value={valueType}>{valueType}</MenuItem>
                      ))}
                    </TextField>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        size="small"
                        label="Unit"
                        value={propertySchemaDraft.unit}
                        onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, unit: event.target.value }))}
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="Cardinality"
                        value={propertySchemaDraft.cardinality}
                        onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, cardinality: event.target.value }))}
                        fullWidth
                      />
                    </Stack>
                    <TextField
                      size="small"
                      select
                      label="Privacy default"
                      value={propertySchemaDraft.privacyDefault}
                      onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, privacyDefault: event.target.value }))}
                    >
                      {PRIVACY_OPTIONS.map((item) => (
                        <MenuItem key={item} value={item}>{item}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      multiline
                      minRows={2}
                      label="Description"
                      value={propertySchemaDraft.description}
                      onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, description: event.target.value }))}
                    />
                    <Typography variant="caption" color="text.secondary">Constraints</Typography>
                    <TextField
                      size="small"
                      label="Pattern (regex)"
                      value={propertySchemaDraft.constraintPattern}
                      onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, constraintPattern: event.target.value }))}
                    />
                    <Stack direction="row" spacing={1}>
                      <TextField
                        size="small"
                        type="number"
                        label="Min"
                        value={propertySchemaDraft.constraintMin}
                        onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, constraintMin: event.target.value }))}
                        fullWidth
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Max"
                        value={propertySchemaDraft.constraintMax}
                        onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, constraintMax: event.target.value }))}
                        fullWidth
                      />
                    </Stack>
                    <TextField
                      size="small"
                      label="Enum (comma-separated)"
                      value={propertySchemaDraft.constraintEnum}
                      onChange={(event) => setPropertySchemaDraft((prev) => ({ ...prev, constraintEnum: event.target.value }))}
                    />
                    <Button
                      variant="contained"
                      disabled={!propertySchemaDraft.key.trim()}
                      onClick={() => void runAction("Save property definition", savePropertyDefinition)}
                    >
                      Save property definition
                    </Button>
                  </>
                )}

                {isToloNote && (
                  <>
                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>TOLO Links</Typography>
                    <TextField
                      size="small"
                      label="Link to existing node"
                      value={linkSearch}
                      onChange={(event) => setLinkSearch(event.target.value)}
                    />
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      {nodeLabelOptions
                        .filter((node) => node.id !== selectedNode.id)
                        .slice(0, 10)
                        .map((node) => (
                          <Chip key={node.id} label={node.label || node.id} onClick={() => void addRefersTo(node.id)} />
                        ))}
                    </Stack>
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      {selectedNodeRefersTo.map((edge) => (
                        <Chip
                          key={edge.id}
                          label={`REFERS_TO: ${nodeById.get(edge.to.nodeId)?.label || edge.to.nodeId}`}
                          onDelete={() => void runAction("Remove REFERS_TO", () => deleteEdge(DEFAULT_GRAPH_ID, edge.id))}
                        />
                      ))}
                    </Stack>
                  </>
                )}

                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>Connected Nodes</Typography>
                {selectedNodeConnections.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No first-order connections.</Typography>
                )}
                {selectedNodeConnections.length > 0 && (
                  <Stack spacing={1.2}>
                    <Stack spacing={0.8}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Outgoing ({outgoingConnectionGroups.reduce((sum, group) => sum + group.rows.length, 0)})
                      </Typography>
                      {outgoingConnectionGroups.length === 0 && (
                        <Typography variant="caption" color="text.secondary">No outgoing edges.</Typography>
                      )}
                      {outgoingConnectionGroups.map((group) => (
                        <Stack key={group.key} spacing={0.6} sx={{ p: 1, border: "1px solid #93c5fd", borderRadius: 1.5, background: "#eff6ff" }}>
                          <Typography variant="caption" color="text.secondary">
                            {group.edgeType} ({group.rows.length})
                          </Typography>
                          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                            {group.rows.map((connection) => (
                              <Chip
                                key={connection.edgeId}
                                label={connection.neighborLabel}
                                onClick={() => focusNode(connection.neighborId)}
                                onDelete={() => void runAction("Delete connected edge", () => deleteEdge(DEFAULT_GRAPH_ID, connection.edgeId))}
                                deleteIcon={<Typography sx={{ color: "#dc2626", fontWeight: 700, px: 0.4 }}>x</Typography>}
                                sx={{ background: "#dbeafe", border: "1px solid #93c5fd" }}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>

                    <Stack spacing={0.8}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Incoming ({incomingConnectionGroups.reduce((sum, group) => sum + group.rows.length, 0)})
                      </Typography>
                      {incomingConnectionGroups.length === 0 && (
                        <Typography variant="caption" color="text.secondary">No incoming edges.</Typography>
                      )}
                      {incomingConnectionGroups.map((group) => (
                        <Stack key={group.key} spacing={0.6} sx={{ p: 1, border: "1px solid #86efac", borderRadius: 1.5, background: "#f0fdf4" }}>
                          <Typography variant="caption" color="text.secondary">
                            {group.edgeType} ({group.rows.length})
                          </Typography>
                          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                            {group.rows.map((connection) => (
                              <Chip
                                key={connection.edgeId}
                                label={connection.neighborLabel}
                                onClick={() => focusNode(connection.neighborId)}
                                onDelete={() => void runAction("Delete connected edge", () => deleteEdge(DEFAULT_GRAPH_ID, connection.edgeId))}
                                deleteIcon={<Typography sx={{ color: "#dc2626", fontWeight: 700, px: 0.4 }}>x</Typography>}
                                sx={{ background: "#dcfce7", border: "1px solid #86efac" }}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}
              </>
            )}

            {selectedEdge && (
              <>
                <Typography variant="subtitle1" fontWeight={700}>Edge</Typography>
                {asOfEnabled && !selectedEdgeValidAsOf && (
                  <Alert
                    severity="warning"
                    action={(
                      <Button size="small" onClick={jumpAsOfToSelectedEdgeTime}>
                        Jump as-of
                      </Button>
                    )}
                  >
                    This item is not valid at the current as-of time; adjust time or disable as-of.
                  </Alert>
                )}
                <TextField
                  size="small"
                  label="Type"
                  value={selectedEdge.type || ""}
                  onChange={(event) => {
                    updateEdge(DEFAULT_GRAPH_ID, selectedEdge.id, { type: event.target.value }).catch(() => {});
                  }}
                />
                <TextField
                  select
                  size="small"
                  label="From node"
                  value={selectedEdge.from.nodeId}
                  onChange={(event) => updateEdge(DEFAULT_GRAPH_ID, selectedEdge.id, { from: { ...selectedEdge.from, nodeId: event.target.value } }).catch(() => {})}
                >
                  {timelineNodes.map((node) => <MenuItem key={node.id} value={node.id}>{node.label || node.id}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="To node"
                  value={selectedEdge.to.nodeId}
                  onChange={(event) => updateEdge(DEFAULT_GRAPH_ID, selectedEdge.id, { to: { ...selectedEdge.to, nodeId: event.target.value } }).catch(() => {})}
                >
                  {timelineNodes.map((node) => <MenuItem key={node.id} value={node.id}>{node.label || node.id}</MenuItem>)}
                </TextField>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="Occurred at"
                  value={edgeTimeDraft.occurredAt}
                  onChange={(event) => setEdgeTimeDraft((prev) => ({ ...prev, occurredAt: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    type="datetime-local"
                    label="Valid from"
                    value={edgeTimeDraft.validFrom}
                    onChange={(event) => setEdgeTimeDraft((prev) => ({ ...prev, validFrom: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="datetime-local"
                    label="Valid to"
                    value={edgeTimeDraft.validTo}
                    onChange={(event) => setEdgeTimeDraft((prev) => ({ ...prev, validTo: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
                {!!edgeTemporalError && <Alert severity="error">{edgeTemporalError}</Alert>}
                <TextField
                  multiline
                  minRows={7}
                  label="Props JSON"
                  value={edgePropsText}
                  onChange={(event) => setEdgePropsText(event.target.value)}
                  error={!validateJSON(edgePropsText).ok}
                  helperText={validateJSON(edgePropsText).ok ? "" : validateJSON(edgePropsText).error}
                />
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={() => void runAction("Save edge", saveEdge)}>Save edge</Button>
                  <Button color="error" variant="outlined" onClick={() => void runAction("Delete edge", () => deleteEdge(DEFAULT_GRAPH_ID, selectedEdge.id))}>Delete edge</Button>
                </Stack>
                <Button onClick={() => void copyText(buildEdgeUri(selectedEdge.id))}>Copy Quilt Link</Button>
              </>
            )}

            {subgraphSession.active && (
              <>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>Subgraph Session</Typography>
                <Typography variant="caption" color="text.secondary">
                  Session nodes: {subgraphSession.nodeIds.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Mouse: double-click canvas to add node. Shift+click two nodes to connect.
                </Typography>
                <TextField
                  size="small"
                  label="Subgraph label"
                  value={subgraphLabelDraft}
                  onChange={(event) => setSubgraphLabelDraft(event.target.value)}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => setSubgraphSelectionNodeIds(visibleNodes.map((node) => node.id))}
                  >
                    Select all visible
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => void runAction(
                      "Save selected to subgraph",
                      () => saveNodesToLabeledSubgraph(subgraphSelectionNodeIds, subgraphLabelDraft)
                    )}
                    disabled={!subgraphSelectionNodeIds.length}
                  >
                    Save selected ({subgraphSelectionNodeIds.length})
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => void runAction(
                      "Save visible to subgraph",
                      () => saveNodesToLabeledSubgraph(visibleNodes.map((node) => node.id), subgraphLabelDraft)
                    )}
                    disabled={!visibleNodes.length}
                  >
                    Save visible ({visibleNodes.length})
                  </Button>
                </Stack>
                <TextField
                  size="small"
                  label="Find saved subgraph"
                  value={subgraphLibrarySearch}
                  onChange={(event) => setSubgraphLibrarySearch(event.target.value)}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    select
                    size="small"
                    label="Reference target"
                    value={subgraphReferenceTargetId}
                    onChange={(event) => setSubgraphReferenceTargetId(event.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">Select subgraph</MenuItem>
                    {subgraphLibraryNodes
                      .filter((node) => node.id !== activeSubgraphId)
                      .map((node) => (
                        <MenuItem key={node.id} value={node.id}>
                          {node.label || node.id}
                        </MenuItem>
                      ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Direction"
                    value={subgraphReferenceDirection}
                    onChange={(event) => setSubgraphReferenceDirection(event.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="out">Outgoing</MenuItem>
                    <MenuItem value="in">Incoming</MenuItem>
                  </TextField>
                </Stack>
                <Button
                  variant="outlined"
                  disabled={!subgraphReferenceTargetId}
                  onClick={() => void runAction("Link subgraphs", linkSubgraphToSubgraph)}
                >
                  Link subgraphs
                </Button>
                {!!activeSubgraphId && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Active subgraph: {allNodeById.get(activeSubgraphId)?.label || activeSubgraphId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      References out: {activeSubgraphOutgoingRefs.length} • in: {activeSubgraphIncomingRefs.length}
                    </Typography>
                  </>
                )}
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  {subgraphLibraryResults.map((node) => (
                    <Chip
                      key={node.id}
                      label={node.label || node.id}
                      onClick={() => openSavedSubgraph(node.id)}
                      color={activeSubgraphId === node.id ? "primary" : "default"}
                      variant={activeSubgraphId === node.id ? "filled" : "outlined"}
                      onDelete={() => {
                        void runAction("Delete subgraph", () => deleteSavedSubgraph(node.id));
                      }}
                    />
                  ))}
                </Stack>
                <TextField
                  size="small"
                  label="Node (search or create)"
                  value={subgraphNodeQuery}
                  onChange={(event) => setSubgraphNodeQuery(event.target.value)}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Edge type (search or create)"
                    value={subgraphConnectType}
                    onChange={(event) => setSubgraphConnectType(event.target.value)}
                    fullWidth
                  />
                  <TextField
                    select
                    size="small"
                    label="Direction"
                    value={subgraphConnectDirection}
                    onChange={(event) => setSubgraphConnectDirection(event.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="out">Outgoing</MenuItem>
                    <MenuItem value="in">Incoming</MenuItem>
                  </TextField>
                </Stack>
                <TextField
                  select
                  size="small"
                  label="Subgraph view mode"
                  value={subgraphViewMode}
                  onChange={(event) => setSubgraphViewMode(event.target.value)}
                >
                  <MenuItem value="Graph">Graph</MenuItem>
                  <MenuItem value="Map">Map</MenuItem>
                </TextField>
                <Button
                  variant="outlined"
                  onClick={() => void runAction("Create node in subgraph session", createNodeInSubgraphSession)}
                >
                  Create node from text
                </Button>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    type="number"
                    label="Hops"
                    value={subgraphHopCount}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      setSubgraphHopCount(Number.isFinite(next) ? Math.max(1, Math.floor(next)) : 1);
                    }}
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ maxWidth: 100 }}
                  />
                  <Button
                    variant="outlined"
                    disabled={!selectedNode}
                    onClick={() => includeNeighborsForSelectedNode(subgraphHopCount)}
                  >
                    Include N-hop neighbors
                  </Button>
                </Stack>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  {subgraphNodeCandidates.map((node) => (
                    <Chip
                      key={node.id}
                      label={node.label || node.id}
                      onClick={() => void runAction("Add/connect node", () => addOrConnectExistingNodeInSubgraphSession(node.id))}
                    />
                  ))}
                </Stack>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  {subgraphEdgeTypeOptions.map((edgeType) => (
                    <Chip
                      key={edgeType}
                      label={edgeType}
                      onClick={() => setSubgraphConnectType(edgeType)}
                    />
                  ))}
                </Stack>
                {!!subgraphLinkSourceId && (
                  <Typography variant="caption" color="text.secondary">
                    Link source: {allNodeById.get(subgraphLinkSourceId)?.label || subgraphLinkSourceId}
                  </Typography>
                )}
                {!!selectedNode && subgraphSession.nodeIds.includes(selectedNode.id) && (
                  <Typography variant="caption" color="text.secondary">
                    Selected anchor: {selectedNode.label || selectedNode.id}
                  </Typography>
                )}
              </>
            )}

            {!selectedNode && !selectedEdge && (
              <Typography variant="body2" color="text.secondary">Select a node or edge.</Typography>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
